import * as React from 'react';
import { Breadcrumb as DefaultBreadcrumb, IBreadcrumbProps, IBreadcrumbItem } from 'office-ui-fabric-react/lib/Breadcrumb';
import { itemsBreadcrumbBetaControl, crumbControl } from '../../controls/itemsBreadcrumbBetaControl';
import { WithItemCache, IItemCacheStateProps } from '../withItemCache/WithItemCache';
import { Omit } from '../../utilities/Omit';
import { IItemCache } from '../../models/IItemCacheObserver';
import { parentFolderItemFacet, displayNameFacet, isSharedFacet } from '../../facets/ItemFacets';
import { ItemOpenAction } from '../actions/open/OpenAction';
import { WithActionSet, IWithActionSetStateProps } from '../withAction/WithAction';
import { withCurrentItemSet, getItemKey, IDecorateWithCurrentItemSetProps } from '../decorators/selectors';
import { WithItemsScope, IItemsScopeStateProps } from '../itemsScope/ItemsScope';
import { withDisplayName } from '../../utilities/components/Decorators';
import { TelemetryProvider } from '../telemetry/TelemetryProvider';
import { IEventContext } from '@ms/telemetry-manager/lib/IEvent';
import { IRenderFunction } from 'office-ui-fabric-react/lib/Utilities';
import { SharedSignal } from '@uifabric/experiments/lib/components/signals/Signals';
import { Fragment } from '../../utilities/components/Fragment';
import { sharedFieldClickActionControl } from '../../controls/sharedFieldClickActionControl';
import * as Styles from './ItemsBreadcrumb.scss';
import { TooltipHost } from 'office-ui-fabric-react/lib/Tooltip';
import strings from '../../models/items/ItemStrings.resx';

export type IItemsBreadcrumbBetaBaseProps = Omit<IBreadcrumbProps, 'items'> & {
  itemKey: string;
};

export type IItemsBreadcrumbBetaProps = IDecorateWithCurrentItemSetProps<IItemsBreadcrumbBetaBaseProps>;

const BREADCRUMB_TELEMETRY_CONTEXT: IEventContext = {
  component: 'Breadcrumb'
};

interface IBreadcrumbItemWithShare extends IBreadcrumbItem {
  onClickShare?: () => void;
}
/**
 * Beta version of ItemsBreadcrumb which uses a cleaner control handler pattern
 * in order to provide flexibility of customization for crumbs and the entire breadcrumb layout.
 */
export const ItemsBreadcrumbBeta: React.ComponentType<IItemsBreadcrumbBetaProps> =
  withDisplayName('ItemsBreadcrumbBeta')(withCurrentItemSet()(
    withDisplayName('ItemsBreadcrumbBeta.Inner')(class extends React.PureComponent<IItemsBreadcrumbBetaBaseProps> {
      public render(): JSX.Element {
        const {
          itemKey: itemSetKey,
          ...breadcrumbProps
        } = this.props;

        return (
          <WithItemsScope>
            { ({ itemsScopeItemKey }: IItemsScopeStateProps) => {
              return (
                <WithItemCache>
                  { ({ itemCache }: IItemCacheStateProps) => {
                    const itemKey = getItemKey(itemCache, {
                      itemKey: itemSetKey
                    }) || itemSetKey;

                    const controlHandler = itemCache.demandItemFacet(itemsBreadcrumbBetaControl, itemKey);
                    const manageAccesscontrolHandler = itemCache.demandItemFacet(sharedFieldClickActionControl, itemKey);
                    const {
                      onClickAction
                    } = sharedFieldClickActionControl.evaluate(manageAccesscontrolHandler)(itemCache, {
                      itemKey: itemKey
                    });

                    const openManageAccessAction = onClickAction && (onClickAction as JSX.Element) || null;

                    const {
                      leafItemKey,
                      crumbControlHandler,
                      breadcrumbAs: Breadcrumb
                    } = itemsBreadcrumbBetaControl.evaluate(controlHandler)(itemCache, {
                      itemKey: itemSetKey,
                      itemsScopeItemKey: itemsScopeItemKey,
                      leafItemKey: itemKey,
                      crumbControlHandler: crumbControl.default,
                      breadcrumbAs: DefaultBreadcrumb
                    });

                    const actions: {
                      [itemKey: string]: JSX.Element | null;
                    } = {};

                    const displayNames: {
                      [itemKey: string]: string | undefined;
                    } = {};

                    const sharedItems: {
                      [itemKey: string]: boolean | undefined;
                    } = {};

                    const itemKeys: string[] = [];

                    let currentItemKey: string | undefined = leafItemKey;

                    while (currentItemKey && !(currentItemKey in actions)) {
                      const {
                        displayName,
                        parentItemKey,
                        action,
                        isSharedItem
                      } = evaluateCrumb(itemCache, {
                        crumbControlHandler,
                        currentItemKey
                      });

                      displayNames[currentItemKey] = displayName;
                      actions[currentItemKey] = action;
                      sharedItems[currentItemKey] = isSharedItem;

                      itemKeys.unshift(currentItemKey);

                      currentItemKey = parentItemKey;
                    }

                    const shareKey = 'share';
                    actions[shareKey] = openManageAccessAction;

                    return (
                      <TelemetryProvider
                        withContext={ BREADCRUMB_TELEMETRY_CONTEXT }
                      >
                        <WithActionSet
                          actions={ actions }
                        >
                          {
                            ({ actions: actionStates }: IWithActionSetStateProps) => {
                              let hasSharedItem: boolean | undefined;
                              const manageAccessActionState = actionStates[shareKey];
                              const onClickToOpenManageAccess = manageAccessActionState ? () => {
                                manageAccessActionState.execute({}).catch(() => {
                                  // Ignore errors.
                                });
                              } : undefined;
                              const items = itemKeys.map((crumbItemKey: string): IBreadcrumbItemWithShare => {
                                const actionState = actionStates[crumbItemKey];
                                const isCurrentItem = actionState && !!actionState.isToggled;
                                const onClick = actionState && actionState.isAvailable ? () => {
                                  actionState.execute({}).catch(() => {
                                    // Ignore errors.
                                  });
                                } : undefined;

                                hasSharedItem = sharedItems[crumbItemKey];
                                return {
                                  text: displayNames[crumbItemKey] || '\u200B',
                                  key: crumbItemKey,
                                  onClick: onClick,
                                  isCurrentItem: isCurrentItem || false,
                                  onClickShare: hasSharedItem && isCurrentItem ? onClickToOpenManageAccess : undefined
                                };
                              });

                              return (
                                <Breadcrumb
                                  { ...breadcrumbProps }
                                  items={ items }
                                  onRenderItem={ (hasSharedItem && this._onRenderBreadcrumbItem) || undefined }
                                />
                              );
                            }
                          }
                        </WithActionSet>
                      </TelemetryProvider>
                    );
                  } }
                </WithItemCache>
              );
            } }
          </WithItemsScope>
        );
      }

      private _onRenderBreadcrumbItem(
        item?: IBreadcrumbItemWithShare,
        defaultRender?: IRenderFunction<IBreadcrumbItemWithShare>
      ): JSX.Element {
        const sharedSignal = item && item.isCurrentItem ? (
          <TooltipHost content={ strings.sharedFolderTooltip } id={ 'breadcrumbSharedIcon' } calloutProps={ { gapSpace: 0 } }>
            <SharedSignal onClick={ item.onClickShare } className={ Styles.sharedPeopleIcon } aria-labelledby={ 'breadcrumbSharedIcon' } />
          </TooltipHost>
        ) : null;
        return (
          <Fragment>
            { defaultRender && item && defaultRender(item) }
            { sharedSignal }
          </Fragment>
        );
      }
    })
  ));

function getParentItemKey(itemCache: IItemCache, props: {
  itemKey: string;
}): string | undefined {
  const parentItem = itemCache.demandItemFacet(parentFolderItemFacet, getItemKey(itemCache, {
    itemKey: props.itemKey
  }));

  return parentItem && parentItem.itemKey;
}

function isShared(itemCache: IItemCache, props: {
  itemKey: string
}): boolean | undefined {
  return itemCache.demandItemFacet(
    isSharedFacet, getItemKey(itemCache, {
      itemKey: props.itemKey
    }));
}

function getDisplayName(itemCache: IItemCache, props: {
  itemKey: string;
}): string | undefined {
  return itemCache.demandItemFacet(displayNameFacet, getItemKey(itemCache, {
    itemKey: props.itemKey
  }));
}

function getAction(itemCache: IItemCache, props: {
  itemKey: string;
}): JSX.Element {
  return (
    <ItemOpenAction itemKey={ props.itemKey } />
  );
}

function evaluateCrumb(itemCache: IItemCache, props: {
  crumbControlHandler: typeof crumbControl.type;
  currentItemKey: string;
}): {
  displayName: string | undefined;
  parentItemKey: string | undefined;
  action: JSX.Element | null;
  isSharedItem: boolean | undefined;
} {
  const {
    currentItemKey,
    crumbControlHandler
  } = props;

  return crumbControl.evaluate(crumbControlHandler)(itemCache, {
    itemKey: currentItemKey,
    displayName: getDisplayName(itemCache, { itemKey: currentItemKey }),
    parentItemKey: getParentItemKey(itemCache, { itemKey: currentItemKey }),
    action: getAction(itemCache, { itemKey: currentItemKey }),
    isSharedItem: isShared(itemCache, { itemKey: currentItemKey })
  });
}
